import React from 'react';
import type { ChatInputState } from '@brandclub/bc-widgets';

/**
 * "Call {assistantName}" CTA on the landing PromptInput.
 *
 * Renders only when `studioConfig.voiceSkills.enabled === true`. The click
 * handler is supplied by the host; after Plan 7a it calls
 * `ConciergeContextProvider.openVoiceCall()` which mounts the full-screen
 * `<VoiceCallOverlay>` above the unified `/concierge?conversationId=:id`
 * route (D-63). The opener allocates a UUID synchronously on the click
 * gesture so iOS Safari preserves the `getUserMedia` user-activation grant.
 *
 * Visual treatment: icon-only button matching the MicrophoneButton footprint
 * (same `styles.iconSize` square, no border, no background, no padding). The
 * full "Call {assistantName}" label is preserved on `aria-label` + `title`
 * so screen readers and tooltip-hover still announce it.
 *
 * IMPORTANT (mobile autoplay / mic permission gating): the click handler
 * runs synchronously on the user's tap; consumers must not await async
 * work before navigating, or browsers will treat `getUserMedia` as
 * non-user-initiated and reject mic permission.
 *
 * Accepts `ctaLabel` so the host can pass any explicit CTA override.
 * When omitted, falls back to the default `Call {assistantName}` rendering.
 * The label is no longer rendered visibly but is still used for
 * `aria-label` / `title`. (Pre-D-61 sourced from
 * `voiceBranding.copy.landingCtaLabel`; that field has been removed.)
 */
export interface CallBennyButtonProps {
  disabled?: boolean;
  onClick: () => void;
  /** Brand persona name; defaults to "Benny". */
  assistantName?: string;
  /**
   * Optional full CTA label override (e.g. host-supplied copy). Used as-is
   * — the caller interpolates `{assistantName}` itself. When omitted, falls
   * back to `Call {assistantName}`. Used for `aria-label` / `title` only —
   * the button has no visible text.
   */
  ctaLabel?: string;
  styles: ChatInputState;
  resolveColor: (color: any) => string;
}

const WaveformIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    style={{ display: 'block' }}
  >
    <path
      d="M8 3a1 1 0 0 1 1 1v16a1 1 0 1 1-2 0V4a1 1 0 0 1 1-1Zm8 2a1 1 0 0 1 1 1v12a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1Zm-4 2a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V8a1 1 0 0 1 1-1ZM4 9a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Zm16 0a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Z"
      fill={color}
    />
  </svg>
);

export const CallBennyButton: React.FC<CallBennyButtonProps> = ({
  disabled = false,
  onClick,
  assistantName = 'Benny',
  ctaLabel,
  styles,
  resolveColor,
}) => {
  const fg = resolveColor(styles.iconColor);
  const iconSize = styles.iconSize;

  const label = ctaLabel || `Call ${assistantName}`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="tw:p-0 tw:bg-transparent tw:border-none tw:cursor-pointer disabled:tw:cursor-not-allowed tw:transition-colors"
      style={{
        width: iconSize,
        height: iconSize,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <WaveformIcon size={iconSize} color={fg} />
    </button>
  );
};

export default CallBennyButton;
